import { useSyncExternalStore } from 'react';

/**
 * A one-shot "focus the side chat's input" request, for an opener that is not a descendant of the chat
 * (the sticky "Ask {maestro}" bar, `lib/sticky-chat-tap.ts`). The dock's composer consumes it: on mount,
 * or when a new request arrives while it is already mounted. Consuming clears it, and it expires on its
 * own: the opened chat may show no composer at all (a survey, a locked channel), and a request left
 * pending would then steal focus in whatever chat mounts a composer next.
 */
const REQUEST_TTL_MS = 2000;

let pending = 0;
let requestedAt = 0;
const listeners = new Set<() => void>();

export function requestChatComposerFocus(): void {
  pending += 1;
  requestedAt = Date.now();
  listeners.forEach((listener) => listener());
}

/** True once per request: the caller focuses its input. */
export function consumeChatComposerFocus(): boolean {
  if (pending === 0) return false;
  pending = 0;
  return Date.now() - requestedAt <= REQUEST_TTL_MS;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Changes on every request, so an effect keyed on it re-runs and consumes it. */
export function useChatComposerFocusRequest(): number {
  return useSyncExternalStore(subscribe, () => pending, () => 0);
}
